/**
 * PostHog Analytics Provider
 *
 * Wraps the app to provide:
 * - PostHog initialization on mount
 * - Automatic $pageview tracking on SPA route changes
 * - User identification when currentUser is available
 * - Reset on logout
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { posthog, initPostHog, identifyUser, resetPostHog } from '@/lib/posthog';
import { useUser } from '@/contexts/UserContext';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';

function PostHogPageviewTracker() {
  const location = useLocation();
  const { currentUser, fullName } = useUser();
  const workspaceId = useWorkspaceId();
  const prevUserIdRef = useRef<string | undefined>();

  // Track pageviews on route changes
  useEffect(() => {
    posthog.capture('$pageview');
  }, [location.pathname, location.search]);

  // Identify user when they become available
  useEffect(() => {
    if (currentUser?.id && currentUser.id !== prevUserIdRef.current) {
      identifyUser({
        id: currentUser.id,
        email: currentUser.email,
        name: fullName,
        role: currentUser.role,
        workspaceId,
      });
      prevUserIdRef.current = currentUser.id;
    }

    // Reset when user logs out
    if (!currentUser?.id && prevUserIdRef.current) {
      resetPostHog();
      prevUserIdRef.current = undefined;
    }
  }, [currentUser?.id, currentUser?.email, currentUser?.role, fullName, workspaceId]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <PHProvider client={posthog}>
      <PostHogPageviewTracker />
      {children}
    </PHProvider>
  );
}
